/* TEWIKY cart discount reset — isolated from add-to-cart, BYJB and analytics.
   After a successful cart LINE mutation, clear manually entered codes on Shopify's
   server (not only in the input), then sync existing cart sections via Horizon's
   native discount-update event. Automatic discounts are not manually removed.
*/
import { CartDiscountUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
let resetQueue = Promise.resolve();
let resetPending = false;
let dispatchingOwnReset = false;
const inFlightDiscounts = new Set();

function appliedCodeVisible() {
  return Boolean(document.querySelector('cart-discount-component .cart-discount__pill'));
}

function hasDiscountCode(cart) {
  return Array.isArray(cart?.discountCodes) &&
    cart.discountCodes.some((entry) => entry?.code && entry.applicable !== false);
}

function clearTypedInputs() {
  document.querySelectorAll('cart-discount-component input[name="discount"]').forEach((input) => {
    input.value = '';
    input.defaultValue = '';
  });
}

function sectionIds() {
  return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((component) => component.dataset.sectionId)
    .filter(Boolean))];
}

function checkoutButtonsDisabled(disabled) {
  document.querySelectorAll('#cart-drawer button[name="checkout"]').forEach((button) => {
    if (disabled) {
      if (!button.disabled) {
        button.disabled = true;
        button.dataset.twkDiscountResetLock = '1';
      }
    } else if (button.dataset.twkDiscountResetLock === '1') {
      button.disabled = false;
      delete button.dataset.twkDiscountResetLock;
    }
  });
}

// Keep the buyer on the cart if the automatic code reset has not finished yet.
document.addEventListener('submit', (event) => {
  if (resetPending && event.target?.id === 'cart-form') {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);

// A buyer may have pressed APPLY immediately before changing a cart line.
// Wait for that request before clearing codes, otherwise it could re-apply
// an old coupon after our reset completes.
document.addEventListener('shopify:cart:discount-update', (event) => {
  if (dispatchingOwnReset || !event.promise) return;
  const pending = Promise.resolve(event.promise).catch(() => {}).finally(() => {
    inFlightDiscounts.delete(pending);
  });
  inFlightDiscounts.add(pending);
});

async function clearAppliedCodes() {
  if (inFlightDiscounts.size) await Promise.allSettled([...inFlightDiscounts]);
  clearTypedInputs();
  const ids = sectionIds();
  const response = await fetch(ROOT + 'cart/update.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      discount: '',
      sections: ids,
      sections_url: window.location.pathname,
    }),
  });
  if (!response.ok) throw new Error('Unable to clear the previous discount code');
  const data = await response.json();
  if (Array.isArray(data.discount_codes) && data.discount_codes.some((code) => code?.applicable)) {
    throw new Error('The previous discount code is still active');
  }
  const deferred = CartDiscountUpdateEvent.createPromise();
  dispatchingOwnReset = true;
  try {
    document.dispatchEvent(new CartDiscountUpdateEvent({
      discountCodes: [],
      promise: deferred.promise,
    }));
  } finally {
    dispatchingOwnReset = false;
  }
  deferred.resolve({
    cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
    detail: { sections: data.sections || {} },
  });
  clearTypedInputs();
  // Let Horizon morph its server-rendered subtotal, code pills and shipping goal.
  await new Promise((resolve) => requestAnimationFrame(resolve));
  clearTypedInputs();
}

document.addEventListener('shopify:cart:lines-update', (event) => {
  if (!['add', 'update', 'remove'].includes(event.action) || !event.promise) return;
  // Capture BEFORE the native cart component optimistically replaces an empty cart.
  const hadCode = appliedCodeVisible();
  Promise.resolve(event.promise).then(({ cart, detail, userErrors }) => {
    if (detail?.didError || userErrors?.length) return;
    const shouldClearServer = hadCode || hasDiscountCode(cart) || appliedCodeVisible();
    resetQueue = resetQueue.catch(() => {}).then(async () => {
      // Preserve the existing add/quantity animation and morph sequence.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      clearTypedInputs();
      if (!shouldClearServer && !appliedCodeVisible()) return;
      resetPending = true;
      checkoutButtonsDisabled(true);
      try {
        await clearAppliedCodes();
      } catch (error) {
        // Never pretend a discount was removed when the server request failed.
        console.warn('[TEWIKY Cart] Discount reset failed:', error);
        const footer = document.querySelector('#cart-drawer [data-twk-cart-footer]');
        if (footer && !footer.querySelector('[data-twk-discount-reset-error]')) {
          const note = document.createElement('p');
          note.dataset.twkDiscountResetError = '1';
          note.setAttribute('role', 'alert');
          note.style.cssText = 'color:#a12a22;font-size:11px;margin:6px 0;';
          note.textContent = 'Discount could not be reset. Please remove the previous code manually before checkout.';
          footer.prepend(note);
        }
      } finally {
        resetPending = false;
        checkoutButtonsDisabled(false);
      }
    });
  }).catch(() => {});
});
